import { API_BASE_URL } from '../config';
import {
  getAccessToken,
  getRefreshToken,
  setAuthSession,
  clearAuthSession,
  getStoredUser,
} from '../utils/authStorage';

let refreshPromise = null;

async function tryRefreshSession() {
  const refresh = getRefreshToken();
  if (!refresh) {
    return false;
  }
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        const payload = await response.json();
        if (!response.ok || Number(payload.code) !== 0) {
          return false;
        }
        const data = payload.data;
        setAuthSession(data.accessToken, data.user, data.refreshToken);
        return true;
      } catch (e) {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/**
 * @param {string} path
 * @param {RequestInit & { body?: object | FormData, _retried?: boolean }} options
 */
export async function request(path, options = {}) {
  const { body, headers = {}, _retried, ...rest } = options;
  const isFormData = body instanceof FormData;
  const token = getAccessToken();
  const init = {
    ...rest,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };
  if (body !== undefined) {
    init.body = isFormData ? body : JSON.stringify(body);
  }
  const url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, init);
  let payload;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    payload = await response.json();
  } else {
    const text = await response.text();
    try {
      payload = JSON.parse(text);
    } catch (e) {
      if (!response.ok) {
        throw new Error(text || `HTTP ${response.status}`);
      }
      payload = text;
    }
  }
  if (response.status === 401 && !_retried && getRefreshToken()) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      return request(path, { ...options, _retried: true });
    }
  }
  if (!response.ok) {
    const msg = (payload && payload.message) || `HTTP ${response.status}`;
    if (response.status === 401) {
      clearAuthSession();
    }
    throw new Error(msg);
  }
  return payload;
}

export function uploadWithProgress(path, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${path}`);
    const token = getAccessToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          reject(new Error('上传响应解析失败'));
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.message || `上传失败 HTTP ${xhr.status}`));
        } catch (e) {
          reject(new Error(`上传失败 HTTP ${xhr.status}`));
        }
      }
    };
    xhr.onerror = () => reject(new Error('网络错误，上传失败'));
    xhr.send(formData);
  });
}

export { getStoredUser };
